import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, FileText, RefreshCw, X } from "lucide-react";
import { Navbar } from "@/components/landing/Navbar";
import { Footer } from "@/components/landing/Footer";
import { Reveal } from "@/components/landing/Reveal";
import { AmbientOrbs } from "@/components/landing/Ambient";
import { UploadIcon3D } from "@/components/landing/UploadIcon3D";
import { ScrollProgress, WhatsAppFab } from "@/components/landing/Floating";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import SEO from "@/components/SEO";

const MAX_REQUIRED_DOCS = 10;

const toServiceIndexEntry = (row) => ({ slug: row.slug, category: row.category, title: row.title, documents: row.sections?.documents || null });

const findService = (serviceIndex, slug, category, title) => {
  const bySlug = serviceIndex.find((s) => s.slug === slug);
  if (bySlug) return bySlug;
  if (title) return { slug: slug || "general", category: category || "General", title, documents: null };
  return null;
};

// Brand palette only (emerald2 / gold) — alternated per row for a subtle highlight, not a rainbow.
const DOC_ROW_COLORS = [
  {
    text: "text-emerald2-600", solidBg: "bg-gradient-to-b from-emerald2-500 to-emerald2-600", border: "border-emerald2-800",
    shadow: "shadow-[0_6px_16px_rgba(16,185,129,0.3)]", onBg: "text-white", iconFrom: "#fef3c7", iconTo: "#d4a24c",
  },
  {
    text: "text-gold-600", solidBg: "bg-gradient-to-b from-gold-400 to-gold-500", border: "border-gold-700",
    shadow: "shadow-[0_6px_16px_rgba(212,162,76,0.35)]", onBg: "text-navy-950", iconFrom: "#e0f2fe", iconTo: "#0f1e3d",
  },
];

// One row per required document: staged-for-upload, already-uploaded (with replace), or not-yet-attached.
const RequiredDocRow = ({ label, index, stagedFile, existingDoc, replacing, onAttach, onRemove, onReplace }) => {
  const color = DOC_ROW_COLORS[index % DOC_ROW_COLORS.length];
  return (
  <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-3.5 py-2.5" data-testid={`required-doc-row-${label}`}>
    <span className="flex min-w-0 items-center gap-2 text-sm font-medium text-navy-700">
      <FileText className={`h-4 w-4 shrink-0 ${color.text}`} />
      <span className="truncate">{label}</span>
    </span>

    {stagedFile ? (
      <span className="flex shrink-0 items-center gap-2 text-xs font-medium text-emerald2-700">
        <CheckCircle2 className="h-4 w-4 shrink-0" />
        <span className="max-w-[9rem] truncate">{stagedFile.name}</span>
        <button type="button" onClick={onRemove} aria-label={`Remove ${stagedFile.name}`} className="text-slate-400 transition-colors hover:text-red-500">
          <X className="h-3.5 w-3.5" />
        </button>
      </span>
    ) : existingDoc ? (
      <span className="flex shrink-0 items-center gap-2 text-xs font-medium" data-testid={`required-doc-uploaded-${label}`}>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald2-500/10 px-2.5 py-1 text-emerald2-700">
          <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
          <span className="max-w-[8rem] truncate">Uploaded: {existingDoc.file_name}</span>
        </span>
        <label className="inline-flex cursor-pointer items-center gap-1 rounded-full border border-navy-900/15 px-2.5 py-1 font-semibold text-navy-800 transition-colors hover:border-gold-400 hover:text-gold-600">
          <RefreshCw className={`h-3 w-3 ${replacing ? "animate-spin" : ""}`} /> {replacing ? "Replacing..." : "Replace"}
          <input
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            className="hidden"
            disabled={replacing}
            data-testid={`required-doc-replace-${label}`}
            onChange={(e) => onReplace(e.target.files?.[0])}
          />
        </label>
      </span>
    ) : (
      <label className={`inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-full border-b-[3px] ${color.border} ${color.solidBg} ${color.shadow} px-3 py-1.5 text-xs font-semibold ${color.onBg} transition-all duration-150 hover:brightness-105 active:translate-y-0.5 active:border-b-0`}>
        <motion.span animate={{ y: [0, -2, 0] }} transition={{ duration: 1.2, repeat: Infinity, ease: "easeInOut" }}>
          <UploadIcon3D className="h-4 w-4" from={color.iconFrom} to={color.iconTo} />
        </motion.span>
        Attach
        <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" data-testid={`required-doc-input-${label}`} onChange={(e) => onAttach(e.target.files?.[0])} />
      </label>
    )}
  </div>
  );
};

export default function Upload() {
  const { user, token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [serviceIndex, setServiceIndex] = useState([]);
  const [requiredFiles, setRequiredFiles] = useState({});
  const [otherFiles, setOtherFiles] = useState([]);
  const [existingDocs, setExistingDocs] = useState([]);
  const [replacingLabel, setReplacingLabel] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    api
      .listServices()
      .then(({ services }) => setServiceIndex((services || []).map(toServiceIndexEntry)))
      .catch(() => {});
  }, []);

  const slug = searchParams.get("service") || "";
  const category = searchParams.get("category") || "";
  const title = searchParams.get("title") || "";

  const service = useMemo(() => findService(serviceIndex, slug, category, title), [serviceIndex, slug, category, title]);
  const requiredLabels = useMemo(
    () => (service?.documents ? Array.from(new Set(service.documents.groups.flatMap((g) => g.items))).slice(0, MAX_REQUIRED_DOCS) : []),
    [service]
  );

  const refreshExistingDocs = () =>
    api
      .myDocuments(token)
      .then(({ documents }) => setExistingDocs(documents || []))
      .catch(() => {});

  useEffect(() => {
    refreshExistingDocs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Latest upload per document label for the currently selected service.
  const existingByLabel = useMemo(() => {
    if (!service) return {};
    const map = {};
    existingDocs
      .filter((d) => d.service_slug === service.slug)
      .forEach((d) => {
        if (!map[d.document_label] || new Date(d.uploaded_at) > new Date(map[d.document_label].uploaded_at)) {
          map[d.document_label] = d;
        }
      });
    return map;
  }, [existingDocs, service]);

  const resetFiles = () => {
    setRequiredFiles({});
    setOtherFiles([]);
    setSubmitted(false);
  };

  const handleServiceChange = (e) => {
    const next = serviceIndex.find((s) => s.slug === e.target.value);
    if (!next) return;
    setSearchParams({ service: next.slug, category: next.category, title: next.title });
    resetFiles();
  };

  const attachRequired = (label, file) => {
    if (!file) return;
    setRequiredFiles((prev) => ({ ...prev, [label]: file }));
  };

  const removeRequired = (label) => {
    setRequiredFiles((prev) => {
      const next = { ...prev };
      delete next[label];
      return next;
    });
  };

  const replaceExisting = async (label, file) => {
    if (!file) return;
    const existingDoc = existingByLabel[label];
    if (!existingDoc) return;
    setReplacingLabel(label);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { document } = await api.replaceDocument(token, existingDoc.id, formData);
      setExistingDocs((prev) => prev.map((d) => (d.id === document.id ? { ...d, ...document } : d)));
      toast.success(`${label} replaced.`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setReplacingLabel(null);
    }
  };

  const handleOtherFilesSelected = (e) => {
    const selected = Array.from(e.target.files || []);
    setOtherFiles((prev) => [...prev, ...selected]);
    e.target.value = "";
  };

  const removeOtherFile = (index) => setOtherFiles((prev) => prev.filter((_, i) => i !== index));

  const totalFileCount = Object.keys(requiredFiles).length + otherFiles.length;

  const onSubmit = async () => {
    if (!service || totalFileCount === 0) return;
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("serviceCategory", service.category);
      formData.append("serviceSlug", service.slug);
      formData.append("serviceTitle", service.title);

      const labels = [];
      Object.entries(requiredFiles).forEach(([label, file]) => {
        formData.append("files", file);
        labels.push(label);
      });
      otherFiles.forEach((file) => {
        formData.append("files", file);
        labels.push("Other Document");
      });
      formData.append("labels", JSON.stringify(labels));

      await api.uploadDocuments(token, formData);
      toast.success(`${totalFileCount} document${totalFileCount > 1 ? "s" : ""} uploaded for ${service.title}.`, {
        description: "Our team will review your documents and get in touch shortly.",
      });
      resetFiles();
      setSubmitted(true);
      refreshExistingDocs();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-white font-sans text-navy-900 antialiased" data-testid="upload-page">
      <SEO
        title="Document Upload | Biz360 Compliance"
        canonical="/upload"
        robots="noindex, nofollow"
      />
      <ScrollProgress />
      <Navbar />
      <main>
        <section className="relative overflow-hidden bg-[rgb(238,242,250)] pt-32 pb-16 md:pt-40 md:pb-20">
          <AmbientOrbs />
          <div className="relative mx-auto max-w-2xl px-5 lg:px-8">
            <Reveal>
              <span className="inline-flex items-center rounded-full border border-emerald2-300/40 bg-emerald2-500/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-emerald2-600">
                Document Upload
              </span>
            </Reveal>
            <Reveal delay={0.05}>
              <h1 className="mt-4 text-3xl font-bold tracking-tight text-navy-900 md:text-4xl">
                Welcome{user ? `, ${user.name.split(" ")[0]}` : ""}
              </h1>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-3 text-base text-slate-600">Upload the documents your engagement needs — we'll pick it up from here.</p>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="mt-8 rounded-3xl border border-slate-200 bg-white p-6 md:p-8 shadow-[0_16px_40px_rgba(15,30,60,0.06)]" data-testid="upload-card">
                <label htmlFor="service-select" className="mb-1.5 block text-sm font-semibold text-navy-800">
                  Service
                </label>
                <select
                  id="service-select"
                  value={service?.slug || ""}
                  onChange={handleServiceChange}
                  data-testid="upload-service-select"
                  className="w-full appearance-none rounded-xl border border-navy-900/15 bg-white px-4 py-3 text-sm text-navy-900 outline-none transition-colors focus:border-gold-400 focus:ring-2 focus:ring-gold-400/20"
                >
                  {!service && <option value="">Select a service</option>}
                  {serviceIndex.map((s) => (
                    <option key={s.slug} value={s.slug}>
                      {s.title}
                    </option>
                  ))}
                </select>

                {requiredLabels.length > 0 && (
                  <div className="mt-5 rounded-2xl bg-[rgb(238,242,250)]/60 p-4" data-testid="upload-required-docs">
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Documents typically required</p>
                    <div className="mt-3 space-y-2">
                      {requiredLabels.map((label, index) => (
                        <RequiredDocRow
                          key={label}
                          label={label}
                          index={index}
                          stagedFile={requiredFiles[label]}
                          existingDoc={existingByLabel[label]}
                          replacing={replacingLabel === label}
                          onAttach={(file) => attachRequired(label, file)}
                          onRemove={() => removeRequired(label)}
                          onReplace={(file) => replaceExisting(label, file)}
                        />
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-6">
                  <label htmlFor="upload-other-files" className="mb-1.5 block text-sm font-semibold text-navy-800">
                    Other Documents <span className="font-normal text-slate-400">(optional — anything not listed above, multiple allowed)</span>
                  </label>
                  <label
                    htmlFor="upload-other-files"
                    data-testid="upload-dropzone"
                    className="flex cursor-pointer flex-col items-center gap-2 rounded-xl border-2 border-dashed border-navy-900/15 bg-white px-4 py-8 text-center transition-colors hover:border-gold-400"
                  >
                    <UploadIcon3D className="h-9 w-9" from="#e0f2fe" to="#0f1e3d" />
                    <span className="text-sm font-medium text-navy-700">Click to upload or drag files here</span>
                    <span className="text-xs text-slate-400">PDF, JPG or PNG — up to 10MB each</span>
                    <input
                      id="upload-other-files"
                      type="file"
                      multiple
                      accept=".pdf,.jpg,.jpeg,.png"
                      className="hidden"
                      data-testid="upload-files-input"
                      onChange={handleOtherFilesSelected}
                    />
                  </label>

                  {otherFiles.length > 0 && (
                    <ul className="mt-3 space-y-1.5" data-testid="upload-file-list">
                      {otherFiles.map((f, i) => (
                        <li key={`${f.name}-${i}`} className="flex items-center justify-between rounded-lg bg-navy-50 px-3 py-2 text-xs text-navy-700">
                          <span className="truncate">{f.name}</span>
                          <button type="button" onClick={() => removeOtherFile(i)} aria-label={`Remove ${f.name}`} className="shrink-0 text-slate-400 transition-colors hover:text-red-500">
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <button
                  type="button"
                  onClick={onSubmit}
                  disabled={submitting || totalFileCount === 0 || !service}
                  data-testid="upload-submit"
                  className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-gold-500 px-7 py-3.5 text-sm font-semibold text-navy-950 shadow-[0_12px_30px_rgba(212,162,76,0.35)] transition-transform duration-300 hover:scale-[1.01] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {submitting ? "Uploading..." : `Submit Documents${totalFileCount > 0 ? ` (${totalFileCount})` : ""}`}
                </button>

                {submitted && (
                  <div className="mt-5 flex items-start gap-2.5 rounded-xl bg-emerald2-500/10 px-4 py-3 text-sm text-emerald2-700" data-testid="upload-success">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>
                      Submitted. Pick another service above to upload more documents, or{" "}
                      <Link to="/account" className="font-semibold underline">
                        view your uploads
                      </Link>
                      .
                    </span>
                  </div>
                )}
              </div>
            </Reveal>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppFab />
    </div>
  );
}
